// Web Audio playback for ICA components.
//
// ICA components are exactly one engine cycle long (samplesPerCycle), so a
// raw playback would be tens to hundreds of milliseconds — not enough to
// "hear" the source. We loop the cycle to a target duration so the source
// repeats audibly.
//
// Each playClip() call gets its own AudioContext that auto-closes when the
// source ends, so multiple components can be auditioned without leaking.

export interface PlayHandle {
  /** Stop playback immediately. */
  stop(): void;
  /** Resolves when playback finishes naturally. */
  done: Promise<void>;
}

export function playLoopedClip(
  samples: number[] | Float32Array,
  sampleRate: number,
  durationSec = 3,
  gain = 1,
): PlayHandle {
  // Tone the AudioContext sample rate to match the source so we don't need
  // to resample. Some browsers ignore the requested rate and fall back to
  // the device default; the AudioBuffer below uses the sample rate the
  // context actually settled on, which means the loop plays at the right
  // pitch regardless.
  const Ctor: typeof AudioContext =
    window.AudioContext ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).webkitAudioContext;
  const ctx = new Ctor({ sampleRate });

  // Normalize to peak = 1 so loud and quiet components play at comparable
  // levels. Without this, weak sources are inaudible while strong ones
  // clip.
  let peak = 0;
  for (let i = 0; i < samples.length; i++) {
    const a = Math.abs(samples[i]);
    if (a > peak) peak = a;
  }
  const norm = peak > 1e-12 ? gain / peak : 0;

  const cycleLen = samples.length;
  const totalSamples = Math.max(cycleLen, Math.ceil(durationSec * ctx.sampleRate));
  const numLoops = Math.ceil(totalSamples / cycleLen);
  const buffer = ctx.createBuffer(1, numLoops * cycleLen, ctx.sampleRate);
  const out = buffer.getChannelData(0);
  for (let loop = 0; loop < numLoops; loop++) {
    const off = loop * cycleLen;
    for (let i = 0; i < cycleLen; i++) out[off + i] = samples[i] * norm;
  }

  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.connect(ctx.destination);

  const done = new Promise<void>((resolve) => {
    source.addEventListener('ended', () => {
      resolve();
      void ctx.close();
    });
  });
  source.start();

  return {
    done,
    stop: () => {
      try { source.stop(); } catch { /* already stopped */ }
    },
  };
}
